// ============================================
// Investor View
// Board-ready investor dashboard with KPIs,
// revenue streams, unit economics, and export
// ============================================

import { useMemo, useState } from 'react';

import {
  TrendingUp,
  DollarSign,
  PieChart,
  Clock,
  Users,
  BarChart3,
  FileDown,
} from 'lucide-react';
import type { Order } from '../../../types/order';

// --- Props ---

interface InvestorViewProps {
  orders: Order[];
  totalRevenueCents: number;
  orderCount: number;
}

// --- Helpers ---

const REVENUE_STATUSES = ['paid', 'processing', 'shipped', 'delivered'] as const;
const DEFAULT_CHURN_RATE = 0.05;

function formatCurrency(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });
}

function formatCurrencyDecimal(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
}

function isCurrentMonth(dateStr: string): boolean {
  const now = new Date();
  const d = new Date(dateStr);
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

// --- Component ---

export function InvestorView({ orders, totalRevenueCents }: InvestorViewProps) {
  const [toastVisible, setToastVisible] = useState(false);

  // Current month paid orders for MRR
  const mrrCents = useMemo(() => {
    return orders
      .filter(
        (o) =>
          REVENUE_STATUSES.includes(o.status as (typeof REVENUE_STATUSES)[number]) &&
          o.paidAt &&
          isCurrentMonth(o.paidAt),
      )
      .reduce((sum, o) => sum + o.totalInCents, 0);
  }, [orders]);

  const arrCents = mrrCents * 12;

  // Revenue by stream
  const revenueStreams = useMemo(() => {
    const revenueOrders = orders.filter((o) =>
      REVENUE_STATUSES.includes(o.status as (typeof REVENUE_STATUSES)[number]),
    );

    // Supplement revenue = total order revenue
    const supplementCents = revenueOrders.reduce((sum, o) => sum + o.totalInCents, 0);

    // Provider consultation revenue = $49 per provider order
    const providerCount = revenueOrders.filter((o) => o.requiresProvider).length;
    const consultationCents = providerCount * 4900;

    const streams: { name: string; cents: number; color: string }[] = [];

    if (supplementCents > 0) {
      streams.push({
        name: 'Supplement Revenue',
        cents: supplementCents,
        color: 'bg-gold',
      });
    }

    if (consultationCents > 0) {
      streams.push({
        name: 'Provider Consultations',
        cents: consultationCents,
        color: 'bg-accent-light',
      });
    }

    return streams;
  }, [orders]);

  const maxStreamCents = useMemo(
    () => Math.max(...revenueStreams.map((s) => s.cents), 1),
    [revenueStreams],
  );

  // Unit Economics
  const unitEconomics = useMemo(() => {
    const revenueOrders = orders.filter((o) =>
      REVENUE_STATUSES.includes(o.status as (typeof REVENUE_STATUSES)[number]),
    );

    // Unique customers by email
    const uniqueEmails = new Set(revenueOrders.map((o) => o.customerEmail));
    const uniqueCustomerCount = uniqueEmails.size;

    const arpuCents =
      uniqueCustomerCount > 0
        ? Math.round(totalRevenueCents / uniqueCustomerCount)
        : 0;

    const ltvCents =
      uniqueCustomerCount > 0
        ? Math.round(arpuCents / DEFAULT_CHURN_RATE)
        : 0;

    return { arpuCents, ltvCents, uniqueCustomerCount };
  }, [orders, totalRevenueCents]);

  // KPI card definitions
  const kpiCards = [
    {
      label: 'ARR',
      value: formatCurrency(arrCents),
      sub: 'Annualized run rate',
      icon: TrendingUp,
      colorClass: 'text-gold',
      bgClass: 'bg-gold/10',
    },
    {
      label: 'MRR',
      value: formatCurrency(mrrCents),
      sub: 'Current month revenue',
      icon: DollarSign,
      colorClass: 'text-optimal',
      bgClass: 'bg-optimal/10',
    },
    {
      label: 'Gross Margin',
      value: '62%',
      sub: 'Industry benchmark (connect QBO for actual)',
      icon: PieChart,
      colorClass: 'text-accent-light',
      bgClass: 'bg-accent-light/10',
    },
    {
      label: 'Runway',
      value: '--',
      sub: 'Connect QBO',
      icon: Clock,
      colorClass: 'text-text-muted',
      bgClass: 'bg-text-muted/10',
    },
  ];

  // Unit Economics card definitions
  const unitCards = [
    {
      label: 'ARPU',
      value: formatCurrencyDecimal(unitEconomics.arpuCents),
      sub: `${unitEconomics.uniqueCustomerCount} unique customers`,
      colorClass: 'text-gold',
    },
    {
      label: 'LTV',
      value: formatCurrency(unitEconomics.ltvCents),
      sub: `At ${(DEFAULT_CHURN_RATE * 100).toFixed(0)}% monthly churn`,
      colorClass: 'text-optimal',
    },
    {
      label: 'CAC',
      value: '--',
      sub: 'Not tracked yet',
      colorClass: 'text-text-muted',
    },
    {
      label: 'LTV:CAC',
      value: '--',
      sub: 'Requires CAC data',
      colorClass: 'text-text-muted',
    },
  ];

  function handleExport() {
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 3000);
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((card, i) => (
          <div
            key={card.label}
            className="anim-fade-in-up bg-bg-card border border-bg-tertiary rounded-xl p-5"
            style={{ animationDelay: `${i * 0.08}s` }}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-text-secondary text-base font-medium">
                {card.label}
              </span>
              <div
                className={`w-10 h-10 rounded-lg ${card.bgClass} flex items-center justify-center`}
              >
                <card.icon size={20} className={card.colorClass} />
              </div>
            </div>
            <p className={`text-3xl font-bold ${card.colorClass}`}>{card.value}</p>
            <p className="text-sm text-text-muted mt-1">{card.sub}</p>
          </div>
        ))}
      </div>

      {/* Revenue by Stream */}
      <div
        className="anim-fade-in-up bg-bg-card border border-bg-tertiary rounded-xl p-5 mb-6"
        style={{ animationDelay: '0.35s' }}
      >
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 size={20} className="text-gold" />
          <h3 className="text-lg font-display text-text-primary">Revenue by Stream</h3>
        </div>

        {revenueStreams.length === 0 ? (
          <p className="text-text-muted text-base">No revenue data yet.</p>
        ) : (
          <div className="space-y-4">
            {revenueStreams.map((stream) => {
              const pct = (stream.cents / maxStreamCents) * 100;
              return (
                <div key={stream.name}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-base text-text-secondary">{stream.name}</span>
                    <span className="text-base font-medium text-text-primary tabular-nums">
                      {formatCurrency(stream.cents)}
                    </span>
                  </div>
                  <div className="w-full bg-bg-elevated rounded-full h-4">
                    <div
                      className={`${stream.color} rounded-full h-4 transition-all duration-500`}
                      style={{ width: `${Math.max(pct, 2)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Unit Economics */}
      <div
        className="anim-fade-in-up bg-bg-card border border-bg-tertiary rounded-xl p-5 mb-6"
        style={{ animationDelay: '0.45s' }}
      >
        <div className="flex items-center gap-2 mb-4">
          <Users size={20} className="text-gold" />
          <h3 className="text-lg font-display text-text-primary">Unit Economics</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {unitCards.map((card) => (
            <div
              key={card.label}
              className="bg-bg-elevated border border-bg-tertiary rounded-lg p-4"
            >
              <p className="text-text-muted text-sm uppercase tracking-wider mb-1">
                {card.label}
              </p>
              <p className={`text-2xl font-bold ${card.colorClass}`}>{card.value}</p>
              <p className="text-sm text-text-muted mt-1">{card.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Export Button */}
      <div
        className="anim-fade-in-up flex justify-end"
        style={{ animationDelay: '0.55s' }}
      >
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-6 py-3 bg-gold text-bg-deep rounded-xl font-medium text-base hover:bg-gold/90 transition-colors focus:outline-none focus:ring-2 focus:ring-gold/50"
        >
          <FileDown size={18} />
          Export for Board
        </button>
      </div>

      {/* Toast */}
      {toastVisible && (
        <div
          className="fixed bottom-6 right-6 bg-bg-elevated border border-gold/30 rounded-xl px-5 py-3 shadow-lg z-50"
        >
          <p className="text-base text-text-primary">PDF export coming soon</p>
        </div>
      )}
    </div>
  );
}
